import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { api } from "../../lib/api";

const STATUSES = ["confirmed", "completed", "cancelled", "no_show", "pending_payment"];

const TONE = {
  confirmed: "bg-teal-500/10 text-teal-300 border-teal-500/20",
  pending_payment: "bg-amber-500/10 text-amber-300 border-amber-500/20",
  completed: "bg-zinc-800 text-mb-fg2",
  cancelled: "bg-zinc-800 text-mb-fg2",
  no_show: "bg-red-500/10 text-red-300 border-red-500/20",
};

export default function DoctorAppointments() {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [status, setStatus] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = { date };
    if (status !== "all") params.status = status;
    api.get("/doctor/appointments", { params })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, [date, status]);

  return (
    <div className="space-y-6" data-testid="doctor-appointments">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-teal-400">◖ appointments</div>
        <h1 className="mt-2 text-3xl font-light tracking-tight">Appointments</h1>
      </div>

      <div className="rounded-xl border border-mb-border bg-mb-surface p-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-mb-fg2">Date</Label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="doc-appt-date"
            className="mt-2 bg-mb-surface2 border-mb-border text-white h-10 rounded-lg" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-mb-fg2">Status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="doc-appt-status" className="mt-2 bg-mb-surface2 border-mb-border text-white h-10 rounded-lg">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-mb-surface2 border-mb-border text-white">
              <SelectItem value="all">All</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="rounded-xl border border-mb-border bg-mb-surface overflow-hidden">
        {loading ? (
          <div className="px-6 py-10 text-sm text-mb-fg2 text-center">Loading…</div>
        ) : items.length === 0 ? (
          <div className="px-6 py-10 text-sm text-mb-fg2 text-center" data-testid="doc-appts-empty">
            No appointments matching filters.
          </div>
        ) : (
          <ul className="divide-y divide-mb-border">
            {items.map((a) => (
              <li key={a.id} className="px-5 py-4 flex flex-col sm:flex-row gap-3 sm:items-center"
                  data-testid={`doc-appt-row-${a.id}`}>
                <div className="text-sm tabular-nums text-mb-fg2 w-32 inline-flex items-center gap-1.5"><Calendar className="h-3 w-3 text-mb-fg2" />
                  {new Date(a.slot_start).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{a.patient?.full_name || "—"}</div>
                  <div className="text-xs text-mb-fg2 truncate">{a.patient?.email}</div>
                </div>
                <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded border ${TONE[a.status] || ""}`}>{a.status.replace("_", " ")}</span>
                <Link to={`/doctor/appointments/${a.id}`} data-testid={`doc-appt-view-${a.id}`}
                  className="text-sm text-teal-400 hover:text-teal-300">Open</Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
